import type { ReactNode } from 'react';
import { ArrowDown, ChevronRight } from 'lucide-react';
import { CircularLoading } from '@zawarski/eti-ui';
import { TablePager } from '@components/DataTable';
import { cn } from '@utils/index';
import { BOTTOM_NAV_HEIGHT, BOTTOM_NAV_HEIGHT_MOBILE } from './BottomNavigation';

export type SortOrder = 'asc' | 'desc';

export interface MuiTableColumn<T> {
  field: string;
  headerName: string;
  numeric?: boolean;
  minWidth?: number;
  renderCell?: (row: T) => ReactNode;
}

interface MuiTableGridProps<T extends Record<string, unknown>> {
  columns: MuiTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  loading?: boolean;
  isMobile?: boolean;
  order: SortOrder;
  orderBy: string;
  onRequestSort: (field: string) => void;
  /** 1-based page; pagination is hidden when `pageCount` is 0 or 1. */
  page?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  /** Phone layout: one card per row (legacy MobileCardView). */
  renderMobileCard?: (row: T) => ReactNode;
  /** Shown in place of the rows when there are none; nothing is shown when omitted. */
  emptyMessage?: string;
  /** Leave room above a bottom navigation bar for the pagination strip. */
  withBottomNav?: boolean;
}

/** Sticky grey head cell, 10px 12px, #474747 on #C2C2C2. */
const HEAD_CELL_CLASS =
  'rep:sticky rep:top-0 rep:z-2 rep:border-b rep:border-[#E0E0E0] rep:bg-[#C2C2C2] rep:px-3 rep:py-2.5 rep:text-sm rep:font-medium rep:text-[#474747]';
/** Body cell: 16px padding, 14px text, the light row divider. */
const BODY_CELL_CLASS = 'rep:border-b rep:border-[#E0E0E0] rep:p-4 rep:text-sm';
const EMPTY_MESSAGE_CLASS = 'rep:px-3 rep:py-4 rep:text-sm rep:text-[#474747]';

/**
 * The legacy paged table (src/components/fragment/MuiTableGrid): sticky grey
 * header with sort labels, zebra rows ending in a chevron, a card list on
 * phones, and a pager strip pinned above the bottom navigation.
 */
const MuiTableGrid = <T extends Record<string, unknown>>({
  columns,
  rows,
  rowKey,
  loading,
  isMobile,
  order,
  orderBy,
  onRequestSort,
  page = 1,
  pageCount = 0,
  onPageChange,
  onRowClick,
  renderMobileCard,
  emptyMessage,
  withBottomNav = true,
}: MuiTableGridProps<T>) => {
  if (loading) {
    return (
      <div className='rep:flex rep:h-[60vh] rep:w-full rep:items-center rep:justify-center'>
        <CircularLoading size='md' />
      </div>
    );
  }

  const paginationBottom = withBottomNav
    ? isMobile
      ? BOTTOM_NAV_HEIGHT_MOBILE
      : BOTTOM_NAV_HEIGHT
    : 0;
  const showEmptyMessage = rows.length === 0 && !!emptyMessage;
  const paged = pageCount > 1 && !!onPageChange;

  return (
    <div className='rep-table-grid rep:flex rep:min-h-0 rep:w-full rep:flex-1 rep:flex-col rep:overflow-hidden rep:bg-white'>
      {isMobile && renderMobileCard ? (
        <div className='rep:min-h-0 rep:w-full rep:flex-1 rep:overflow-auto rep:pb-18'>
          {showEmptyMessage ? (
            <div className={cn(EMPTY_MESSAGE_CLASS, 'rep:text-center')}>{emptyMessage}</div>
          ) : null}
          {rows.map((row) => (
            <div
              key={rowKey(row)}
              className={cn(
                'rep-table-row rep:relative rep:box-border rep:flex rep:w-full rep:gap-0.5 rep:overflow-hidden rep:bg-white rep:p-2',
                onRowClick ? 'rep:cursor-pointer' : 'rep:cursor-default',
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}>
              <div className='rep:flex rep:w-full rep:flex-col rep:justify-center rep:gap-0.5 rep:rounded rep:bg-[rgb(235,235,235)] rep:p-2'>
                {renderMobileCard(row)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className={cn(
            'rep:box-border rep:min-h-0 rep:w-full rep:flex-1 rep:overflow-auto',
            paged ? 'rep:pb-18' : 'rep:pb-4',
          )}>
          <table className='rep:w-full rep:min-w-175 rep:border-separate rep:border-spacing-0'>
            <thead>
              <tr>
                {columns.map((column) => {
                  const active = orderBy === column.field;
                  return (
                    <th
                      key={column.field}
                      scope='col'
                      aria-sort={
                        active ? (order === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                      className={cn(
                        HEAD_CELL_CLASS,
                        column.numeric ? 'rep:text-right' : 'rep:text-center',
                      )}
                      style={{ minWidth: column.minWidth }}>
                      <button
                        type='button'
                        className='rep:group rep:inline-flex rep:cursor-pointer rep:items-center rep:gap-1 rep:font-medium'
                        onClick={() => onRequestSort(column.field)}>
                        {column.headerName}
                        <ArrowDown
                          aria-hidden='true'
                          className={cn(
                            'rep:size-4.5 rep:transition-all',
                            active ? 'rep:opacity-100' : 'rep:opacity-0 rep:group-hover:opacity-50',
                            active && order === 'asc' ? 'rep:rotate-180' : '',
                          )}
                        />
                      </button>
                    </th>
                  );
                })}
                <th className={HEAD_CELL_CLASS} style={{ width: 20 }} aria-label='Open' />
              </tr>
            </thead>
            <tbody>
              {showEmptyMessage ? (
                // Left-aligned: the table is wider than most screens, so a centered
                // message would land off to the right.
                <tr>
                  <td colSpan={columns.length + 1} className={EMPTY_MESSAGE_CLASS}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : null}
              {rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={cn(
                    'rep-table-row rep:odd:bg-black/4 rep:[&:last-child>td]:border-b-0',
                    onRowClick ? 'rep:cursor-pointer' : 'rep:cursor-default',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}>
                  {columns.map((column) => (
                    <td
                      key={column.field}
                      className={cn(
                        BODY_CELL_CLASS,
                        column.numeric ? 'rep:text-right' : 'rep:text-left',
                      )}
                      style={{ minWidth: column.minWidth }}>
                      {column.renderCell
                        ? column.renderCell(row)
                        : ((row[column.field] as ReactNode) ?? '')}
                    </td>
                  ))}
                  <td className={BODY_CELL_CLASS}>
                    <ChevronRight className='rep:size-5' />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paged ? (
        <div
          className='palmetto-print-hide rep:absolute rep:right-0 rep:left-0 rep:z-1000 rep:box-border rep:w-full rep:border-t rep:border-[#E0E0E0] rep:bg-white rep:px-4 rep:py-3'
          style={{ bottom: paginationBottom, boxShadow: '0 -2px 4px rgba(0,0,0,0.1)' }}>
          <TablePager page={page} pageCount={pageCount} onPageChange={onPageChange} />
        </div>
      ) : null}
    </div>
  );
};

export default MuiTableGrid;
